/*
 * pokemonDetailDexOcr.ts
 *
 * Pokémon GO **Pokédex species detail page** OCR (Pro feature).
 *
 * This is the "one screenshot updates every dex for a Pokémon" scanner. It
 * reads the species detail page — the screen you land on when you tap a
 * Pokémon inside your in-game Pokédex — which shows ONE species and a grid of
 * every dex VARIANT for it (Shiny / 100% / Lucky / XXL / XXS / Mega / G-Max /
 * Shadow / Purified), each rendered as a card that is either a full-colour
 * sprite (you own that variant) or a flat blue silhouette (you don't).
 *
 * It is the transpose of `pokedexOcr.ts`: that module reads ONE variant across
 * MANY species (a scrolled grid column of the Pokédex); this module reads MANY
 * variants for ONE species. Both write the same `dex_records` store through
 * `dexStateStore`.
 *
 *   extractPokemonDetailPage(dataUrl) -> PokemonDetailFrame
 *   applyDetailFrame(frame)           -> writes each variant to dexStateStore
 *
 * ── How a card's owned/not-owned state is read ──────────────────────────────
 * The dex NUMBER (e.g. "0025") and the card LABELS ("SHINY", "LUCKY", …) are
 * printed text, so identifying the species and each variant is ordinary OCR —
 * we reuse `VARIANT_TOKENS` from the shared variant classifier. The owned state
 * is NOT text: it is the full-colour-sprite-vs-blue-silhouette distinction, so
 * it is a pixel classification (`classifyDetailCard`).
 *
 * The silhouette classifier was calibrated against real device screenshots
 * (client/src/test/fixtures/detail/, 39 hand-labelled cards across 5 species).
 * Two signals separate the two states with a wide, clean margin:
 *
 *              nonBlueFrac   activeHueBuckets
 *   silhouette   0.000            1              (a single flat blue fill)
 *   owned      ≥ 0.071         ≥ 2              (a shaded, multi-hue sprite)
 *
 * `nonBlueFrac` is the fraction of chromatic pixels whose hue falls OUTSIDE the
 * silhouette blue band; a real sprite always has some (eyes, belly, shading)
 * even for a blue Pokémon, whereas the silhouette is a solid single-colour
 * shape and reads exactly 0. See scripts/detail_fixture_report notes and the
 * fixture test `pokemonDetailDexOcr.fixture.test.ts`.
 *
 * ── What we deliberately do NOT do ──────────────────────────────────────────
 *  - We NEVER fabricate a variant we could not see. Cards scrolled below the
 *    fold or occluded simply do not appear in `variants`; they are never
 *    written "missing". A single screenshot updates the VISIBLE dexes only.
 *  - Regional forms (Alolan/Galarian/…) each have their own grid shown one at
 *    a time. We tag observations with the selected form's `formId` only when we
 *    can confirm a non-base form tile is selected; otherwise we default to the
 *    base form and surface a warning, so we can never mis-write a form dex.
 *  - "Costume" is not a Nexus dex variant, so a Costume card is ignored.
 */

import { loadFrameImage } from "./frameImageCache";
import {
  recognizeRegions,
  type OcrRegion,
  type OcrTextBlock,
} from "./ocrEngine";
import {
  isDerivedVariant,
  VARIANT_TOKENS,
  type PokedexVariant,
  type CellState,
} from "./pogoPokedexVariantClassifier";
import { applyScanObservation, getDexEntryState } from "./dexStateStore";
import { isVariantAvailable } from "./variantAvailability";
import { FORM_GROUPS_BY_SPECIES_ID } from "./generated/pokemonForms.generated";
import { speciesName } from "../data/speciesNames";
import { CANVAS_READBACK } from "./canvasReadback";

// ── Types ───────────────────────────────────────────────────────────────────

/** Raw silhouette-vs-sprite signals for one card, surfaced for diagnostics. */
export interface DetailCardSignals {
  /** Fraction of the sample box that is chromatic (not near-grey background). */
  chromaFrac: number;
  /** Distinct 30° hue buckets carrying ≥6% of the chromatic mass. */
  activeHueBuckets: number;
  /** Fraction of chromatic pixels whose hue is OUTSIDE the silhouette blue band. */
  nonBlueFrac: number;
  /** Variance of per-pixel luminance over the sample box (0-1²). */
  lumVar: number;
  /** Mean saturation over chromatic pixels. */
  meanSat: number;
  /** Dominant hue bucket centre in degrees, or null when achromatic. */
  domHue: number | null;
  /**
   * Share of the chromatic mass in each of the twelve 30° hue buckets
   * (index k covers [30k, 30k+30)). Lets the decision tell WASH-coloured
   * buckets from sprite-coloured ones (2026-09-16); absent on signals built
   * before that, which fall back to `activeHueBuckets`.
   */
  hueShares?: number[];
  /**
   * The card's OWN background hue, sampled from the four corners of the body
   * box (2026-09-16), or null when the corners disagree or are achromatic.
   * The page wash is a gradient (peach at the top, pink by the bottom row), so
   * a card two rows down sits on a tint some 30° off the strip sample; its
   * silhouette is drawn in the card's own tint, which this is.
   */
  localWashHue?: number | null;
}

export interface DetailCardVerdict {
  /** present = owned (colour sprite); missing = silhouette; unknown = no read. */
  state: CellState;
  /** 0-1 confidence in the verdict, from the margin to the decision boundary. */
  confidence: number;
  signals: DetailCardSignals;
}

/** One variant card read off the detail page. */
export interface DetailVariantObservation {
  variant: PokedexVariant;
  state: CellState;
  /** Raw card label ("SHINY", "MEGA X", …); empty string for the default card. */
  label: string;
  /** Normalised full-image ROI of the card body we classified. */
  roi: OcrRegion;
  confidence: number;
  signals: DetailCardSignals;
}

/**
 * One form tile read off the selector under the species name. Each form's
 * caught state is encoded by its tile sprite (colour = registered/caught, grey
 * or blue silhouette = unregistered). Writes to the base "pokemon" dex for that
 * form's `formId`.
 */
export interface DetailFormObservation {
  /** Form id (e.g. "raichu-alola"), or null for the species' base form. */
  formId: string | null;
  /** Display name ("Alolan", "Base"). */
  label: string;
  /** present = registered sprite, missing = silhouette, unknown = unreadable. */
  state: CellState;
  /** True when this tile carries the white selection ring (its grid is shown). */
  selected: boolean;
  roi: OcrRegion;
  confidence: number;
}

export interface PokemonDetailFrame {
  /** True when the frame parses as a species detail page (vs a grid / other). */
  isDetailPage: boolean;
  /** National dex number read from the title, or null. */
  dexNumber: number | null;
  /** Species name read from the title (UPPERCASE, as printed), or null. */
  name: string | null;
  /** Selected regional form id (e.g. "sandslash-alola"), or null for base form. */
  formId: string | null;
  /** Display name of the selected form ("Alolan", …), or null for base form. */
  formLabel: string | null;
  /** True when this species shows an in-game form selector (base + regional tiles). */
  formSelectorPresent: boolean;
  /** Every form tile read from the selector (base + regionals). Empty for single-form species. */
  forms: DetailFormObservation[];
  /** Every variant card we confidently located and classified. */
  variants: DetailVariantObservation[];
  /** 0-100 overall confidence (title read × card-classification margins). */
  confidence: number;
  warnings: string[];
}

// ── Calibrated pixel thresholds ──────────────────────────────────────────────
// Derived from client/src/test/fixtures/detail/ (see file header). Keep the
// wide margins: never tighten a threshold to the far side of a real sample.

/** Below this per-pixel chroma-max delta a pixel is background/grey (no hue). */
const HUE_GREY_FLOOR = 0.1;
/** A hue bucket is "active" when it carries at least this share of chroma mass. */
const BUCKET_ACTIVE_MASS = 0.06;
/** Blue band [lo, hi] in degrees — kept for the diagnostic nonBlueFrac signal. */
const BLUE_BAND_LO = 175;
const BLUE_BAND_HI = 225;
/** A card with less chromatic coverage than this is too empty to call. */
const MIN_CHROMA_FOR_READ = 0.2;
/** …but a small-bodied sprite (Caterpie, Weedle, …) fills so little of its card that
 *  chromaFrac drops under MIN_CHROMA_FOR_READ even though it is plainly caught. A
 *  coherent multi-hue, non-wash colour cluster above this smaller floor still counts as
 *  a real catch (see decideSprite) — below it we keep calling it empty to reject noise. */
const MIN_CHROMA_SMALL_SPRITE = 0.03;
//
// Wash-relative silhouette rule. In PoGo the detail page is tinted by the
// SELECTED variant card: the default view is cyan, but selecting Shiny/… tints
// the whole page (and its silhouettes) gold/etc. A silhouette is therefore a
// flat single-hue fill in the PAGE-WASH colour, not specifically blue. So we
// classify a uniform tile as a silhouette when its dominant hue matches the
// page wash. Validated across 59 real cards+tiles on cyan and gold pages.
//
/** Owned iff ≥ this many active hue buckets (a multi-colour sprite). */
const SPRITE_MULTI_HUE_BUCKETS = 2;
/** Owned iff at least this achromatic fraction (a pale/white real sprite has white pixels; a saturated wash-colour silhouette does not). */
const SPRITE_ACHROMATIC_MIN = 0.05;
/** A uniform fill is a silhouette when its dominant hue is within this of the page wash. */
const SILHOUETTE_WASH_HUE_TOL = 30;
/**
 * A mono-colour real sprite on a wash-tinted card: the sprite's own hue is a
 * minority of the chroma (the tinted card fills the rest; a small Pikachu is
 * 6-7% of its XXL card on the cyan view), so "present" needs one active
 * non-wash bucket, OR this much non-wash chroma spread thinly across several
 * buckets none of which reaches the active floor on its own.
 */
const SPRITE_NON_WASH_MASS_MIN = 0.12;
/** Each corner patch sampled for the card's own tint spans this fraction of the box. */
const LOCAL_WASH_CORNER_FRAC = 0.14;
/**
 * The corner pixels must agree on a hue this tightly (circular-mean resultant
 * length, 1 = identical) before their tint counts as the card's background: a
 * sprite reaching into a corner spreads the hues and the read is dropped.
 */
const LOCAL_WASH_MIN_CONCENTRATION = 0.9;
/**
 * The card tint is always the page tint's own family (the wash is a gradient
 * down the page, not a second colour), so a corner read further than this
 * from the page wash is a sprite filling the corners, not a background, and
 * is ignored. A tan sprite that fills its box on the cyan view stays a sprite.
 */
const LOCAL_WASH_MAX_DRIFT = 60;
/** Default wash hue when the page background could not be sampled (cyan default view). */
const DEFAULT_WASH_HUE = 200;
/** Page wash hue band that counts as the reliable default (cyan) view. Outside it the page is tinted by a selected variant tab and variant-grid reads are less reliable. */
const CYAN_WASH_LO = 175;
const CYAN_WASH_HI = 215;

// ── Form-selector geometry + selection-ring thresholds ───────────────────────
// The in-game form selector (base + regional tiles) sits in the fixed header
// above the type/gender row, so its normalised geometry is stable across scroll
// positions. The selected tile carries a bright white rounded border; the
// unselected tile does not. Calibrated against Raichu/Sandslash/Sandshrew (all
// base-selected): the regional tile's white-border fraction reads ~0.01-0.03
// when unselected and ~0.12-0.14 when selected. See the form-selection fixture
// test. The regional tile is located directly above its printed form label.
const FORM_TILE_LABEL_GAP = 0.043; // regional label centre → tile body centre (Δy)
const FORM_TILE_HALF_W = 0.072;
const FORM_TILE_HALF_H = 0.036;
const FORM_RING_INNER_PAD = 0.018; // skip the deep tile interior when ringing
const FORM_RING_OUTER_PAD = 0.006; // include a hair outside the tile edge
/** Near-white pixel: bright and low-saturation (the selection border colour). */
const FORM_RING_WHITE_LUM = 0.75;
const FORM_RING_WHITE_SAT = 0.15;
/** A tile whose border ring is at least this white is the selected tile. */
const FORM_TILE_SELECTED_MIN = 0.06; // selected ~0.12+, unselected ~0.03-
/** Regional label centre → base tile centre (Δx): one tile pitch to the left. */
const FORM_TILE_PITCH = 0.174;
/** Central sprite-body sample half-size for a form tile (avoids border/bg). */
const FORM_TILE_BODY_HALF_W = 0.045;
const FORM_TILE_BODY_HALF_H = 0.026;

/**
 * Region handed to the OCR engine. Starts exactly at the status-bar exclusion
 * (0.06) so `recognizeRegions`' internal clamp is a no-op and returned block
 * coordinates map back to the full frame with a single affine transform.
 */
const SCAN_ROI: OcrRegion = { x: 0, y: 0.06, w: 1, h: 0.94 };

// ── Pure: silhouette-vs-sprite card classifier ───────────────────────────────

/**
 * Classify one card/tile body region (already cropped to RGBA) as an owned
 * colour sprite (`present`) or an unowned silhouette (`missing`), relative to
 * the page wash hue. Pure over a raw RGBA buffer so it unit-tests in Node
 * (sharp → raw) and runs verbatim in the browser (canvas getImageData).
 *
 * Decision (see the wash-relative note above the thresholds):
 *   - `unknown` when the sample is mostly background (too empty to call).
 *   - `present` when the sprite is multi-hue (≥2 buckets) or carries pale/white
 *     (achromatic) content a saturated silhouette never has.
 *   - `missing` when it is a uniform fill whose dominant hue matches the page
 *     wash (a wash-coloured silhouette: blue on cyan pages, tan on gold pages).
 *   - `present` otherwise (a uniform fill in a hue OTHER than the wash is a
 *     mono-colour real sprite, not a silhouette).
 *
 * `washHue` defaults to the cyan default-view wash when omitted.
 */
export function classifyDetailCard(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  washHue: number | null = DEFAULT_WASH_HUE,
): DetailCardVerdict {
  return decideSprite(detailCardSignalsOf(data, width, height), washHue);
}

/**
 * Classify a FORM-SELECTOR tile (the small sprite under the species name for
 * each form: base, Alolan, …). Identical logic to `classifyDetailCard` — a form
 * tile is a sprite-or-silhouette just like a variant card — kept as a named
 * entry point for the form-reading call site.
 */
export function classifyFormTile(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  washHue: number | null = DEFAULT_WASH_HUE,
): DetailCardVerdict {
  return decideSprite(detailCardSignalsOf(data, width, height), washHue);
}

/**
 * Shared wash-relative sprite-vs-silhouette decision over precomputed signals.
 *
 * WASH-AWARE HUE COUNTING (2026-09-16, the Arboliva XXL report). A card is
 * tinted in the page wash and its silhouette is drawn in that same tint, so
 * every hue within tolerance of a wash (the page strip's, or the card's own
 * corners) is BACKGROUND, not sprite. Only the remaining hue buckets count
 * toward "multi-colour sprite". The old count took every active bucket, and a
 * pink page's tint straddles the 0°/360° seam of the hue wheel, so one flat
 * pink silhouette landed in two buckets and read as a caught XXL.
 */
function decideSprite(
  signals: DetailCardSignals,
  washHue: number | null,
): DetailCardVerdict {
  const wash = washHue ?? DEFAULT_WASH_HUE;
  const washes = [wash];
  if (signals.localWashHue != null && hueDistance(signals.localWashHue, wash) <= LOCAL_WASH_MAX_DRIFT) {
    washes.push(signals.localWashHue);
  }
  const isWashHue = (h: number): boolean =>
    washes.some((w) => hueDistance(h, w) <= SILHOUETTE_WASH_HUE_TOL);
  // Non-wash hue structure: how many buckets, and how much of the chroma.
  let nonWashActive: number;
  let nonWashMass: number;
  if (signals.hueShares && signals.hueShares.length === 12) {
    nonWashActive = 0;
    nonWashMass = 0;
    signals.hueShares.forEach((share, k) => {
      if (isWashHue(k * 30 + 15)) return;
      nonWashMass += share;
      if (share > BUCKET_ACTIVE_MASS) nonWashActive += 1;
    });
  } else {
    // Signals built without the histogram: the pre-2026-09-16 reading, where
    // every active bucket counted and only the dominant hue was wash-checked.
    const domIsWash = signals.domHue != null && isWashHue(signals.domHue);
    nonWashActive = domIsWash ? Math.max(0, signals.activeHueBuckets - 1) : signals.activeHueBuckets;
    nonWashMass = domIsWash ? 0 : 1;
    if (signals.activeHueBuckets >= SPRITE_MULTI_HUE_BUCKETS) nonWashActive = SPRITE_MULTI_HUE_BUCKETS;
  }
  // Small-bodied sprites (e.g. Caterpie, Weedle) fill so little of the card that
  // chromaFrac drops below MIN_CHROMA_FOR_READ even though they are plainly caught, so
  // the empty-card gate below would drop them as "unknown" and never write the catch.
  // Rescue them when the card carries a coherent MULTI-HUE colour cluster that is NOT the
  // page wash — a structural signature a single-hue silhouette (or JPEG noise on a truly
  // empty card) cannot fake — above a smaller chroma floor. Fixes the Caterpie/Weedle
  // detail-scan false-negative (2026-08-03).
  const smallSprite =
    signals.chromaFrac >= MIN_CHROMA_SMALL_SPRITE &&
    nonWashActive >= SPRITE_MULTI_HUE_BUCKETS;
  if (signals.chromaFrac < MIN_CHROMA_FOR_READ && !smallSprite) {
    return { state: "unknown", confidence: 0, signals };
  }
  if (nonWashActive >= SPRITE_MULTI_HUE_BUCKETS) {
    const bucketMargin = clamp01((nonWashActive - 1) / 2);
    return { state: "present", confidence: clamp01(0.7 + 0.3 * bucketMargin), signals };
  }
  const achromatic = 1 - signals.chromaFrac;
  if (achromatic >= SPRITE_ACHROMATIC_MIN) {
    // A pale/white real sprite: some achromatic content a saturated silhouette lacks.
    return { state: "present", confidence: clamp01(0.6 + achromatic), signals };
  }
  // A mono-colour real sprite in a hue OTHER than the wash, sitting on the
  // tinted card: its own hue is a minority share of the chroma, but a real one.
  if (nonWashActive >= 1 || nonWashMass >= SPRITE_NON_WASH_MASS_MIN) {
    return { state: "present", confidence: clamp01(0.6 + nonWashMass), signals };
  }
  // Uniform saturated single-hue fill: silhouette iff it matches a wash.
  if (signals.domHue != null && isWashHue(signals.domHue)) {
    const nearest = Math.min(...washes.map((w) => hueDistance(signals.domHue!, w)));
    const margin = 1 - nearest / SILHOUETTE_WASH_HUE_TOL;
    return { state: "missing", confidence: clamp01(0.6 + 0.4 * margin), signals };
  }
  // Uniform fill in a non-wash hue → a mono-colour real sprite.
  return { state: "present", confidence: 0.6, signals };
}

/** Smallest angular distance (degrees) between two hues on the colour wheel. */
function hueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Compute the raw silhouette-vs-sprite signals for an RGBA sample box. With
 * the box's `width` × `height` the four corners are also sampled for the
 * card's own tint (`localWashHue`); without them that read is skipped.
 */
export function detailCardSignalsOf(
  data: Uint8ClampedArray | Uint8Array,
  width?: number,
  height?: number,
): DetailCardSignals {
  const buckets = new Array<number>(12).fill(0);
  let chroma = 0;
  let total = 0;
  let blueChroma = 0;
  let lumSum = 0;
  let lumSq = 0;
  let satSum = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    const r = data[i] / 255;
    const g = data[i + 1] / 255;
    const b = data[i + 2] / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    lumSum += lum;
    lumSq += lum * lum;
    total += 1;
    if (d < HUE_GREY_FLOOR) continue; // background / grey: contributes no hue
    const sat = max <= 0 ? 0 : d / max;
    satSum += sat;
    let hh: number;
    if (max === r) hh = ((g - b) / d) % 6;
    else if (max === g) hh = (b - r) / d + 2;
    else hh = (r - g) / d + 4;
    hh *= 60;
    if (hh < 0) hh += 360;
    buckets[Math.floor(hh / 30) % 12] += 1;
    chroma += 1;
    if (hh >= BLUE_BAND_LO && hh <= BLUE_BAND_HI) blueChroma += 1;
  }

  const safeTotal = Math.max(1, total);
  const meanLum = lumSum / safeTotal;
  const lumVar = Math.max(0, lumSq / safeTotal - meanLum * meanLum);
  const chromaFrac = chroma / safeTotal;
  const nonBlueFrac = chroma ? 1 - blueChroma / chroma : 0;
  const meanSat = chroma ? satSum / chroma : 0;
  let domIdx = 0;
  for (let k = 1; k < 12; k++) if (buckets[k] > buckets[domIdx]) domIdx = k;
  const domHue = chroma ? domIdx * 30 + 15 : null;
  const activeHueBuckets = chroma
    ? buckets.filter((c) => c / chroma > BUCKET_ACTIVE_MASS).length
    : 0;

  const hueShares = chroma ? buckets.map((c) => round3(c / chroma)) : buckets.map(() => 0);
  const localWashHue =
    width != null && height != null && width * height * 4 <= data.length
      ? cornerWashHue(data, width, height)
      : null;

  return {
    chromaFrac: round3(chromaFrac),
    activeHueBuckets,
    nonBlueFrac: round3(nonBlueFrac),
    lumVar: round4(lumVar),
    meanSat: round3(meanSat),
    domHue,
    hueShares,
    localWashHue,
  };
}

/**
 * The card's own background tint: the circular-mean hue of the chromatic
 * pixels in the four corner patches of the body box, or null when they are
 * achromatic (a white card on the cyan default view reads as no tint) or
 * disagree (a sprite reaching a corner). Corners are judged one at a time
 * and a corner the sprite reaches is simply dropped, so a wide sprite costs
 * a corner, not the read; at least two clean corners must agree.
 * See `DetailCardSignals.localWashHue`.
 */
function cornerWashHue(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): number | null {
  const pw = Math.max(1, Math.floor(width * LOCAL_WASH_CORNER_FRAC));
  const ph = Math.max(1, Math.floor(height * LOCAL_WASH_CORNER_FRAC));
  const corner = (x0: number, y0: number): number | null => {
    let sx = 0;
    let sy = 0;
    let n = 0;
    let total = 0;
    for (let y = y0; y < y0 + ph; y++) {
      for (let x = x0; x < x0 + pw; x++) {
        const i = (y * width + x) * 4;
        total += 1;
        const r = data[i] / 255;
        const g = data[i + 1] / 255;
        const b = data[i + 2] / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const d = max - min;
        if (d < 0.03) continue;
        let hh: number;
        if (max === r) hh = ((g - b) / d) % 6;
        else if (max === g) hh = (b - r) / d + 2;
        else hh = (r - g) / d + 4;
        hh *= 60;
        if (hh < 0) hh += 360;
        const rad = (hh * Math.PI) / 180;
        sx += Math.cos(rad);
        sy += Math.sin(rad);
        n += 1;
      }
    }
    // Mostly achromatic: an untinted card corner, no tint to report.
    if (n === 0 || n < total * 0.6) return null;
    // Mixed hues: the sprite is in this corner, not the background.
    if (Math.hypot(sx, sy) / n < LOCAL_WASH_MIN_CONCENTRATION) return null;
    let hue = (Math.atan2(sy, sx) * 180) / Math.PI;
    if (hue < 0) hue += 360;
    return hue;
  };
  const hues = [
    corner(0, 0),
    corner(width - pw, 0),
    corner(0, height - ph),
    corner(width - pw, height - ph),
  ].filter((h): h is number => h != null);
  if (hues.length < 2) return null;
  let sx = 0;
  let sy = 0;
  for (const h of hues) {
    sx += Math.cos((h * Math.PI) / 180);
    sy += Math.sin((h * Math.PI) / 180);
  }
  let mean = (Math.atan2(sy, sx) * 180) / Math.PI;
  if (mean < 0) mean += 360;
  // The clean corners must all be the same tint.
  if (hues.some((h) => hueDistance(h, mean) > 20)) return null;
  return mean;
}

// ── Pure: label → variant mapping ────────────────────────────────────────────

/**
 * Map a printed card label to a Nexus dex variant, or null when the label is
 * not a scannable variant. Reuses the shared `VARIANT_TOKENS` regexes so the
 * detail page and the grid scanner agree on spellings, plus the detail-page
 * specifics: "MEGA X"/"MEGA Y" both fold to `mega`, and "COSTUME" (not a Nexus
 * variant) resolves to null so its card is skipped.
 */
export function mapDetailLabelToVariant(text: string): PokedexVariant | null {
  const t = (text || "").trim();
  if (!t) return null;
  if (/costume/i.test(t)) return null; // not a Nexus dex variant
  for (const [variant, regexes] of VARIANT_TOKENS) {
    for (const re of regexes) {
      if (re.test(t)) return variant;
    }
  }
  return null;
}

// ── Pure: card geometry from label anchors ───────────────────────────────────

/**
 * A card sits directly above its printed label. Given the label's full-image
 * box, return the normalised ROI of the card BODY to classify — a central box
 * that avoids the card's rounded border and the small variant badge icon in the
 * top-left corner. Calibrated against the fixture layout (see file header).
 */
export function cardBodyRoiFromLabel(labelBox: OcrTextBlock): OcrRegion {
  const cx = labelBox.x + labelBox.w / 2;
  // The label baseline sits ~0.006 below the card bottom; the card body is a
  // roughly square tile ~0.075 of frame height. Sample its central column.
  const BODY_W = 0.11;
  const BODY_H = 0.075;
  const cardBottom = labelBox.y - 0.006;
  const cyCenter = cardBottom - BODY_H / 2 - 0.012;
  return clampRoi({
    x: cx - BODY_W / 2,
    y: cyCenter - BODY_H / 2,
    w: BODY_W,
    h: BODY_H,
  });
}

/**
 * Infer the default (top-left, unlabelled) card's body ROI from the top row of
 * labelled cards. The default card is column 0 of the top card row; the labels
 * to its right (Shiny, 100%, Lucky …) give both the row height and the column
 * pitch. Returns null when fewer than two top-row labels were found.
 */
export function defaultCardRoiFromTopRow(
  topRowLabels: OcrTextBlock[],
): OcrRegion | null {
  if (topRowLabels.length < 2) return null;
  const sorted = [...topRowLabels].sort((a, b) => a.x - b.x);
  const first = sorted[0];
  const second = sorted[1];
  const c0 = first.x + first.w / 2;
  const c1 = second.x + second.w / 2;
  const pitch = c1 - c0;
  if (!(pitch > 0.05)) return null; // implausible column pitch
  const body = cardBodyRoiFromLabel(first);
  const defaultCx = c0 - pitch;
  return clampRoi({ x: defaultCx - body.w / 2, y: body.y, w: body.w, h: body.h });
}

// ── Pure: detail-page detector ───────────────────────────────────────────────

/**
 * Decide whether a set of OCR text blocks belongs to a species detail page.
 * The signature is stable across scroll positions: the CAUGHT/INFO/BATTLE tab
 * row, the SEEN + CAUGHT stat labels, and a zero-padded dex number in the top
 * half. We require the tab row (or SEEN+CAUGHT) plus at least one variant label
 * so a grid page (which has none of these) never matches.
 */
export function looksLikePokemonDetailPage(blocks: OcrTextBlock[]): boolean {
  const upper = blocks.map((b) => (b.text || "").toUpperCase());
  const has = (re: RegExp) => upper.some((t) => re.test(t));
  // The CAUGHT / INFO / BATTLE tab row is unique to the detail page — a grid
  // page never shows INFO or BATTLE. Require ≥2 of the three tab words (OCR can
  // drop one), or the SEEN+CAUGHT stat pair plus at least one tab word, so a
  // grid upload (which has neither INFO nor BATTLE) can never be misrouted here.
  const tabScore = [/\bCAUGHT\b/, /\bINFO\b/, /\bBATTLE\b/].filter((re) =>
    has(re),
  ).length;
  const seenCaught = has(/\bSEEN\b/) && has(/\bCAUGHT\b/);
  const tabSignature = tabScore >= 2 || (seenCaught && tabScore >= 1);
  const hasVariantLabel = blocks.some((b) => mapDetailLabelToVariant(b.text) != null);
  // Permissive: any multi-digit run (the title, or a stat count). Detection just
  // needs to know this is a detail page; readTitle does the precise title parse.
  const hasDexNumber = blocks.some((b) => /\d{2,5}/.test(b.text || ""));
  return tabSignature && hasVariantLabel && hasDexNumber;
}

// ── Pure: title / form parsing ───────────────────────────────────────────────

/** Parse a zero-padded national dex number ("0025", "0003"). */
export function parseDexNumber(text: string): number | null {
  const m = (text || "").trim().match(/^0*(\d{1,4})$/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 1 && n <= 1025 ? n : null;
}

const REGIONAL_FORM_KEYWORDS: Array<[RegExp, string]> = [
  [/\bALOLAN?\b/i, "alola"],
  [/\bGALARIAN?\b/i, "galar"],
  [/\bHISUIAN?\b/i, "hisui"],
  [/\bPALDEAN?\b/i, "paldea"],
];

/** Match a regional-form label to its slug fragment ("alola"), or null. */
export function regionalFormSlugFromLabel(text: string): string | null {
  for (const [re, slug] of REGIONAL_FORM_KEYWORDS) {
    if (re.test(text || "")) return slug;
  }
  return null;
}

/**
 * Resolve a concrete formId for a species + regional slug using the generated
 * forms table (e.g. dex 28 + "alola" → "sandslash-alola"). Returns null when the
 * species has no such form, so we never invent a form dex row.
 */
export function resolveRegionalFormId(
  dexNumber: number,
  slug: string,
): string | null {
  const group = FORM_GROUPS_BY_SPECIES_ID.get(dexNumber);
  if (!group) return null;
  const hit = group.forms.find((f) => f.formId.endsWith(`-${slug}`));
  return hit ? hit.formId : null;
}

// ── Pure: transpose to store observations ────────────────────────────────────

export interface DetailDexWrite {
  variant: PokedexVariant;
  dexNumber: number;
  state: CellState;
  formId: string | null;
}

/**
 * Transpose a detail frame into the per-variant writes
 * `dexStateStore.applyScanObservation` consumes:
 *   - Each FORM tile → the base "pokemon" dex for that form's id (base + every
 *     regional form the selector shows), so one scan updates every form.
 *   - Each VARIANT card (Shiny/Lucky/…) → that variant on the SELECTED form's
 *     id (the grid always belongs to the selected form).
 * When a form selector is present the grid's own default "pokemon" card is
 * dropped — the form tiles are the authority for base-catch state. `unknown`
 * cells and cells with no dex number are never written.
 */
export function detailFrameToWrites(frame: PokemonDetailFrame): DetailDexWrite[] {
  if (frame.dexNumber == null) return [];
  const dexNumber = frame.dexNumber;
  const out: DetailDexWrite[] = [];
  const hasFormTiles = frame.forms.length > 0;
  for (const v of frame.variants) {
    if (v.state === "unknown") continue;
    // The form tiles supersede the grid's default base card when present.
    if (v.variant === "pokemon" && hasFormTiles) continue;
    out.push({ variant: v.variant, dexNumber, state: v.state, formId: frame.formId });
  }
  for (const f of frame.forms) {
    if (f.state === "unknown") continue;
    out.push({ variant: "pokemon", dexNumber, state: f.state, formId: f.formId });
  }
  return out;
}

/**
 * Build the two-line scan confirmation for a detail frame:
 *   line1: "Pikachu (0025)"
 *   line2: "All forms updated" (multi-form) / "All dexes updated" (single form)
 * Used by both the in-app toast and the PiP HUD so the feedback is identical.
 */
export function detailScanFeedback(
  frame: PokemonDetailFrame,
  applied?: number,
): {
  line1: string;
  line2: string;
} {
  const dex = frame.dexNumber ?? 0;
  const name = frame.name
    ? titleCaseName(frame.name)
    : speciesName(dex);
  const line1 = `${name} (${String(dex).padStart(4, "0")})`;
  // When we know how many dex rows actually changed, say so — an honest count
  // ("6 dexes updated") beats a blanket "All dexes updated" that fires even when
  // nothing was written. applied === 0 means every owned variant was already on
  // file (or nothing was readable), not that the dexes changed.
  const line2 =
    applied != null
      ? applied > 0
        ? `${applied} ${applied === 1 ? "dex" : "dexes"} updated`
        : "Already up to date"
      : frame.formSelectorPresent
        ? "All forms updated"
        : "All dexes updated";
  return { line1, line2 };
}

function titleCaseName(upper: string): string {
  const s = upper.trim();
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export interface ApplyDetailResult {
  applied: number;
  skippedManual: number;
  present: number;
  missing: number;
  /** "missing" writes refused because the dex already says PRESENT. */
  protected: number;
}

export interface DetailFramePreview {
  /** Owned variants this dex ALREADY has on file — saving changes nothing. */
  caught: number;
  /** Rows this save would actually change. */
  toUpdate: number;
}

/**
 * What saving this frame would do, read BEFORE saving it.
 *
 * The review sheet used to report the screenshot alone — "8 owned · 0 not yet" —
 * which says what the page showed, not what pressing Save does to your dex. A
 * Koffing page with eight owned variants of which six were already recorded read
 * "8 owned", saved, and then said "2 dexes updated": two different true numbers,
 * and the one shown first was the one nobody could act on. Reported on device
 * 2026-08-12.
 *
 * The split is by STATE — what your dex says now versus what the screenshot
 * says. A row already in the state we would write is "caught", not an update,
 * even in the corner where the store technically rewrites it to change a manual
 * row's provenance to `scan`. The number people are reading is "what is about to
 * change about my collection", and that is nothing.
 *
 * Writes the store would refuse outright are excluded from both counts, so the
 * sheet never promises an update that cannot land: a derived dex is read-only,
 * and a "present" for a species GO has not released is rejected by the scanner
 * integrity guard (see applyScanObservation).
 */
export function previewDetailFrame(frame: PokemonDetailFrame): DetailFramePreview {
  let caught = 0;
  let toUpdate = 0;
  for (const w of detailFrameToWrites(frame)) {
    if (isDerivedVariant(w.variant)) continue;
    if (!Number.isFinite(w.dexNumber) || w.dexNumber < 1) continue;
    if (w.state === "present" && !isVariantAvailable(w.variant, w.dexNumber)) continue;
    const prev = getDexEntryState(w.variant, w.dexNumber, w.formId);
    if (prev?.state === w.state) {
      if (w.state === "present") caught += 1;
      continue;
    }
    // Mirror applyDetailFrame's no-un-catch rule: a "missing" verdict over a
    // present row will be refused there, so the sheet must not promise it.
    if (w.state === "missing" && prev?.state === "present") {
      caught += 1;
      continue;
    }
    toUpdate += 1;
  }
  return { caught, toUpdate };
}

/**
 * Persist a detail frame to the dex store. Each variant flows through the same
 * `applyScanObservation` path the grid/live scanners use, so the write is
 * last-write-wins (it overwrites an older manual/scan row rather than deferring
 * to it) and the MAIN-dex cascade fires for owned sub-variants.
 *
 * WITH ONE ASYMMETRY, and it is load-bearing (2026-08-28 device report): a
 * detail frame may CATCH, it may never UN-CATCH. A "missing" verdict is a
 * pixel classification — a silhouette check that glare, a screen-recording
 * overlay, or a mid-flicker frame can fail on a card the trainer owns — and
 * the live path can re-apply the same species across session restarts (each
 * restart clears the gate's applied set). Two shaky dwells were enough to
 * wipe whole pages that an earlier clean dwell had just written. So a
 * "missing" write lands only where the dex does not already say "present";
 * a present row is only ever un-caught by the trainer's own hand (manual
 * edit), never by a frame. The refusals are counted (`protected`) and
 * logged, so a genuinely stale tick is visible rather than silently kept.
 */
export function applyDetailFrame(frame: PokemonDetailFrame): ApplyDetailResult {
  const writes = detailFrameToWrites(frame);
  let applied = 0;
  let present = 0;
  let missing = 0;
  let protectedCount = 0;
  for (const w of writes) {
    if (w.state === "missing") {
      const prev = getDexEntryState(w.variant, w.dexNumber, w.formId);
      if (prev?.state === "present") {
        protectedCount += 1;
        // eslint-disable-next-line no-console
        console.log("[dex-scan] detail downgrade refused", {
          variant: w.variant,
          dexNumber: w.dexNumber,
          formId: w.formId ?? null,
          existingSource: prev.source ?? null,
        });
        continue;
      }
    }
    // Dex writes are otherwise last-write-wins: an upload simply states what
    // it saw, nothing needs to out-rank anything.
    const ok = applyScanObservation(w.variant, w.dexNumber, w.state, w.formId);
    if (ok) {
      applied += 1;
      if (w.state === "present") present += 1;
      else if (w.state === "missing") missing += 1;
    } else {
      // WHY a detail write did not land. Without this, "OCR read it correctly
      // but the dex didn't change" is unfalsifiable from a device log — it can't
      // distinguish a BLOCKED write from a write that landed but isn't rendering.
      // Under last-write-wins the store no longer skips for a manual override, so
      // the only real skips are: rejected before the store (derived variant /
      // invalid id / unknown state), or an identical scan re-observation
      // (`already-in-that-state`, meaning the store DOES have it and any problem
      // is downstream — grid read / roster — not the write). existingSource is
      // logged as provenance only.
      const prev = getDexEntryState(w.variant, w.dexNumber, w.formId);
      // eslint-disable-next-line no-console
      console.log("[dex-scan] detail write skipped", {
        variant: w.variant,
        dexNumber: w.dexNumber,
        wanted: w.state,
        formId: w.formId ?? null,
        existingState: prev?.state ?? null,
        existingSource: prev?.source ?? null,
        reason:
          prev == null
            ? "rejected-before-store (derived variant / invalid id / unknown state)"
            : prev.state === w.state
              ? "already-in-that-state"
              : "unknown",
      });
    }
  }
  return {
    applied,
    skippedManual: writes.length - applied - protectedCount,
    present,
    missing,
    protected: protectedCount,
  };
}

// ── OCR orchestration (browser) ──────────────────────────────────────────────

export interface DetailOcrDeps {
  loadImage: (dataUrl: string) => Promise<HTMLImageElement>;
  ocrBlocks: (img: HTMLImageElement, roi: OcrRegion) => Promise<OcrTextBlock[]>;
  cropRgba: (
    img: HTMLImageElement,
    roi: OcrRegion,
  ) => { data: Uint8ClampedArray; width: number; height: number } | null;
}

/**
 * Read a species detail page and return a structured frame. The three
 * browser-only primitives (image decode, OCR, canvas crop) are injected so the
 * pure parsing/geometry/classification logic is unit-testable in Node.
 */
export async function extractPokemonDetailPage(
  dataUrl: string,
  deps: DetailOcrDeps = defaultDeps(),
): Promise<PokemonDetailFrame> {
  const warnings: string[] = [];
  const empty: PokemonDetailFrame = {
    isDetailPage: false,
    dexNumber: null,
    name: null,
    formId: null,
    formLabel: null,
    formSelectorPresent: false,
    forms: [],
    variants: [],
    confidence: 0,
    warnings,
  };
  if (!dataUrl) return empty;

  let img: HTMLImageElement;
  try {
    img = await deps.loadImage(dataUrl);
  } catch {
    warnings.push("image_decode_failed");
    return empty;
  }

  const rawBlocks = await deps.ocrBlocks(img, SCAN_ROI);
  // Map ROI-relative block coords back into full-frame normalised coords.
  const blocks = rawBlocks.map((b) => mapBlockToFrame(b, SCAN_ROI));

  if (!looksLikePokemonDetailPage(blocks)) {
    return empty;
  }

  // Title: the zero-padded dex number, and the UPPERCASE name on its row.
  const { dexNumber, name } = readTitle(blocks);
  if (dexNumber == null) {
    warnings.push("dex_number_unread");
    return { ...empty, isDetailPage: true, name, warnings };
  }

  // Page wash hue: the detail page is tinted by the selected variant card
  // (cyan by default, gold when Shiny is selected, …). Silhouettes are drawn in
  // this wash colour, so classification is done relative to it. When the page
  // is NOT the cyan default view, the variant-grid sprite/silhouette split gets
  // unreliable for wash-coloured Pokémon, so we drop risky "missing" reads.
  const washHue = pageWashHue(img, deps);
  const tinted =
    washHue != null && (washHue < CYAN_WASH_LO || washHue > CYAN_WASH_HI);
  if (tinted) warnings.push("tinted_view");

  // Forms: read every form tile under the name (base + regionals). Each tile's
  // sprite encodes that form's caught state; the selection ring marks which
  // form the variant grid below belongs to.
  const { forms, selectedFormId, selectedLabel } = readForms(
    blocks,
    dexNumber,
    img,
    deps,
    washHue,
  );

  // Variant cards: one observation per labelled card + the default card. These
  // belong to the selected form. On a tinted page we keep only confident
  // present reads and never assert a "missing" (a wash-coloured sprite can read
  // as a silhouette), so we never wrongly mark a caught variant uncaught.
  const variants = classifyVariantCards(blocks, img, deps, warnings, washHue, tinted);

  if (variants.length === 0) warnings.push("no_variant_cards");

  const cardConf =
    variants.length > 0
      ? variants.reduce((s, v) => s + v.confidence, 0) / variants.length
      : 0;
  const confidence = Math.round(clamp01(0.5 + 0.5 * cardConf) * 100);

  return {
    isDetailPage: true,
    dexNumber,
    name,
    formId: selectedFormId,
    formLabel: selectedLabel,
    formSelectorPresent: forms.length > 0,
    forms,
    variants,
    confidence,
    warnings,
  };
}

// ── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Words that appear beside a number in the header but are NOT the species name
 * (stat labels, types, toggles). Used to reject a "count + label" pair so the
 * title parser never mistakes a SEEN/CAUGHT count for the dex number.
 */
const TITLE_LABEL_WORDS = new Set<string>([
  "SEEN", "CAUGHT", "INFO", "BATTLE", "ALERT", "NORMAL", "COSTUME", "OFF", "ON",
  "SHINY", "LUCKY", "PERFECT", "MEGA", "GMAX", "SHADOW", "PURIFIED", "XXL", "XXS",
  "GRASS", "POISON", "FIRE", "WATER", "ELECTRIC", "GROUND", "FLYING", "PSYCHIC",
  "ICE", "DRAGON", "DARK", "FAIRY", "FIGHTING", "ROCK", "BUG", "GHOST", "STEEL",
  "ALOLAN", "GALARIAN", "HISUIAN", "PALDEAN",
]);

/** First 3-5 digit run in a string parsed as a national dex id (1-1025), or null. */
function dexNumberInText(s: string): number | null {
  const m = (s || "").match(/\d{3,5}/);
  if (!m) return null;
  const n = parseInt(m[0], 10);
  return Number.isFinite(n) && n >= 1 && n <= 1025 ? n : null;
}

/** The first uppercase alphabetic run of a candidate name block, cleaned, or null. */
function cleanTitleName(s: string): string | null {
  const m = (s || "").toUpperCase().match(/[A-Z][A-Z.'♀♂-]{1,}/);
  if (!m) return null;
  const name = m[0].replace(/[^A-Z0-9'.♀♂-]/g, "");
  return name.length >= 2 ? name : null;
}

/**
 * Read the species title (dex number + name) from the header.
 *
 * The title is uniquely a 4-digit zero-padded number IMMEDIATELY followed by
 * the UPPERCASE species name on one line ("⊙ 0001 BULBASAUR"). Apple Vision on
 * device returns that whole line — pokeball glyph, number, and name — as ONE
 * observation, so the earlier "block is all digits" parse failed and the code
 * fell back to a bare SEEN/CAUGHT count (the on-device #0090/#0774/#0057 bug).
 *
 * We instead require the number-adjacent-to-name pattern, which a stat count
 * (a lone number under a "SEEN"/"CAUGHT" label) never has, so a count can never
 * be mistaken for the dex number.
 */
export function readTitle(blocks: OcrTextBlock[]): {
  dexNumber: number | null;
  name: string | null;
} {
  const region = blocks
    .filter((b) => b.y < 0.5)
    .sort((a, b) => a.y - b.y); // topmost first — the title sits above the stats

  // 1) Combined block: "0001 BULBASAUR" (optionally with a leading glyph).
  for (const b of region) {
    const m = (b.text || "").match(/(\d{3,5})\s*([A-Za-z][A-Za-z.'♀♂\- ]*)/);
    if (!m) continue;
    const n = dexNumberInText(m[1]);
    const name = cleanTitleName(m[2]);
    if (n != null && name && !TITLE_LABEL_WORDS.has(name)) {
      return { dexNumber: n, name };
    }
  }

  // 2) Split blocks: a number-only title block + the uppercase name to its
  //    right on the same row (the shape our synthetic fixtures use).
  for (const b of region) {
    const t = (b.text || "").trim();
    if (!/^\W*\d{3,5}\W*$/.test(t)) continue; // number-only block
    const n = dexNumberInText(t);
    if (n == null) continue;
    const cy = b.y + b.h / 2;
    const nameBlock = region.find(
      (o) =>
        o !== b &&
        Math.abs(o.y + o.h / 2 - cy) < 0.05 &&
        o.x + o.w / 2 > b.x &&
        /[A-Za-z]{2,}/.test(o.text || "") &&
        !TITLE_LABEL_WORDS.has(cleanTitleName(o.text) ?? ""),
    );
    const name = nameBlock ? cleanTitleName(nameBlock.text) : null;
    if (name) return { dexNumber: n, name };
  }

  return { dexNumber: null, name: null };
}

/**
 * Read the form selector under the species name. Returns one observation per
 * form tile (base + every regional form the selector shows), each with its
 * caught state (from the tile sprite) and whether it is the selected tile (from
 * the white selection ring). Also returns the selected form's id + label, which
 * the variant grid writes are attributed to.
 *
 * Single-form species (no regional label on screen) return an empty `forms`
 * list; the caller then treats the page as base-only.
 */
function readForms(
  blocks: OcrTextBlock[],
  dexNumber: number,
  img: HTMLImageElement,
  deps: DetailOcrDeps,
  washHue: number | null,
): {
  forms: DetailFormObservation[];
  selectedFormId: string | null;
  selectedLabel: string | null;
} {
  // Regional-form labels in the header band (above the tab row at ~0.57; the
  // selector can sit as low as ~0.51 on tall-header phones), each resolving to
  // a real form.
  const regionalLabels = blocks
    .filter((b) => b.y < 0.55 && regionalFormSlugFromLabel(b.text) != null)
    .map((b) => {
      const slug = regionalFormSlugFromLabel(b.text)!;
      const formId = resolveRegionalFormId(dexNumber, slug);
      return formId ? { block: b, formId } : null;
    })
    .filter((x): x is { block: OcrTextBlock; formId: string } => x != null)
    .sort((a, b) => a.block.x - b.block.x);

  if (regionalLabels.length === 0) {
    return { forms: [], selectedFormId: null, selectedLabel: null };
  }

  const group = FORM_GROUPS_BY_SPECIES_ID.get(dexNumber);
  const displayFor = (formId: string): string =>
    group?.forms.find((f) => f.formId === formId)?.displayName ?? "Form";

  // The regional tiles share a row; the base tile is one pitch left of the
  // leftmost regional tile. Build tile centres from the label anchors.
  const tileCy = regionalLabels[0].block.y - FORM_TILE_LABEL_GAP;
  const leftmostRegionalCx =
    regionalLabels[0].block.x + regionalLabels[0].block.w / 2;

  interface Tile {
    formId: string | null;
    label: string;
    cx: number;
  }
  const tiles: Tile[] = [
    { formId: null, label: "Base", cx: leftmostRegionalCx - FORM_TILE_PITCH },
    ...regionalLabels.map((r) => ({
      formId: r.formId,
      label: displayFor(r.formId),
      cx: r.block.x + r.block.w / 2,
    })),
  ];

  // Classify each tile: sprite state + selection-ring score.
  const rings = tiles.map((t) => formTileRingWhite(img, deps, t.cx, tileCy));
  const maxRing = Math.max(...rings);
  const selectedIdx =
    maxRing >= FORM_TILE_SELECTED_MIN ? rings.indexOf(maxRing) : 0; // default base

  const forms: DetailFormObservation[] = tiles.map((t, i) => {
    const bodyRoi = clampRoi({
      x: t.cx - FORM_TILE_BODY_HALF_W,
      y: tileCy - FORM_TILE_BODY_HALF_H,
      w: FORM_TILE_BODY_HALF_W * 2,
      h: FORM_TILE_BODY_HALF_H * 2,
    });
    const rgba = deps.cropRgba(img, bodyRoi);
    const verdict = rgba
      ? classifyFormTile(rgba.data, rgba.width, rgba.height, washHue)
      : { state: "unknown" as CellState, confidence: 0 };
    return {
      formId: t.formId,
      label: t.label,
      state: verdict.state,
      selected: i === selectedIdx,
      roi: bodyRoi,
      confidence: verdict.confidence,
    };
  });

  const selected = forms[selectedIdx];
  return {
    forms,
    selectedFormId: selected.formId,
    selectedLabel: selected.formId ? selected.label : null,
  };
}

/**
 * White-border fraction of a form tile's selection ring. The selected tile
 * carries a bright white rounded border; the unselected tile does not. Samples
 * the ring straddling the tile boundary (skipping the deep interior) and
 * returns the fraction of near-white (bright, low-saturation) pixels.
 */
function formTileRingWhite(
  img: HTMLImageElement,
  deps: DetailOcrDeps,
  cx: number,
  cy: number,
): number {
  const outer = clampRoi({
    x: cx - FORM_TILE_HALF_W - FORM_RING_OUTER_PAD,
    y: cy - FORM_TILE_HALF_H - FORM_RING_OUTER_PAD,
    w: (FORM_TILE_HALF_W + FORM_RING_OUTER_PAD) * 2,
    h: (FORM_TILE_HALF_H + FORM_RING_OUTER_PAD) * 2,
  });
  const rgba = deps.cropRgba(img, outer);
  if (!rgba) return 0;
  const { data, width, height } = rgba;
  // Inner exclusion rectangle in pixel coords (skip the deep tile interior).
  const inX0 = (FORM_RING_OUTER_PAD + FORM_RING_INNER_PAD) / outer.w;
  const inX1 = 1 - inX0;
  const inY0 = (FORM_RING_OUTER_PAD + FORM_RING_INNER_PAD) / outer.h;
  const inY1 = 1 - inY0;
  let white = 0;
  let total = 0;
  for (let py = 0; py < height; py++) {
    const fy = py / height;
    for (let px = 0; px < width; px++) {
      const fx = px / width;
      if (fx > inX0 && fx < inX1 && fy > inY0 && fy < inY1) continue; // interior
      const i = (py * width + px) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      const mx = Math.max(r, g, b);
      const mn = Math.min(r, g, b);
      const sat = mx ? (mx - mn) / mx : 0;
      total += 1;
      if (lum > FORM_RING_WHITE_LUM && sat < FORM_RING_WHITE_SAT) white += 1;
    }
  }
  return total ? white / total : 0;
}

/**
 * Locate every variant label, classify the card above it, and add the default
 * (unlabelled) card. Deduplicates by variant, keeping the higher-confidence
 * read (Mega X/Y both fold to `mega` — a present tile wins over a silhouette).
 */
function classifyVariantCards(
  blocks: OcrTextBlock[],
  img: HTMLImageElement,
  deps: DetailOcrDeps,
  warnings: string[],
  washHue: number | null,
  tinted: boolean,
): DetailVariantObservation[] {
  // On a tinted page a wash-coloured sprite can read as a silhouette, so a
  // "missing" verdict is untrustworthy — demote it to "unknown" (dropped) so we
  // only ever record confident present reads there.
  const guard = (v: DetailCardVerdict): DetailCardVerdict =>
    tinted && v.state === "missing" ? { ...v, state: "unknown" } : v;

  // Only labels in the grid area (below the tab row, which sits ~0.5-0.6).
  const labelBlocks = blocks
    .filter((b) => b.y > 0.55)
    .map((b) => ({ b, variant: mapDetailLabelToVariant(b.text) }))
    .filter((x) => x.variant != null) as Array<{
    b: OcrTextBlock;
    variant: PokedexVariant;
  }>;

  const byVariant = new Map<PokedexVariant, DetailVariantObservation>();
  const keep = (obs: DetailVariantObservation) => {
    const prev = byVariant.get(obs.variant);
    if (!prev) {
      byVariant.set(obs.variant, obs);
      return;
    }
    // Prefer a present read, then higher confidence — a species with two Mega
    // tiles (X/Y) is "has mega" if either is owned.
    const prevScore = (prev.state === "present" ? 1 : 0) + prev.confidence;
    const nextScore = (obs.state === "present" ? 1 : 0) + obs.confidence;
    if (nextScore > prevScore) byVariant.set(obs.variant, obs);
  };

  for (const { b, variant } of labelBlocks) {
    const roi = cardBodyRoiFromLabel(b);
    const rgba = deps.cropRgba(img, roi);
    if (!rgba) continue;
    const verdict = guard(classifyDetailCard(rgba.data, rgba.width, rgba.height, washHue));
    keep({
      variant,
      state: verdict.state,
      label: b.text.trim(),
      roi,
      confidence: verdict.confidence,
      signals: verdict.signals,
    });
  }

  // Default card (column 0 of the top card row) → the base "pokemon" dex.
  const topRow = topRowLabelBlocks(labelBlocks.map((x) => x.b));
  const defaultRoi = defaultCardRoiFromTopRow(topRow);
  if (defaultRoi) {
    const rgba = deps.cropRgba(img, defaultRoi);
    if (rgba) {
      const verdict = guard(classifyDetailCard(rgba.data, rgba.width, rgba.height, washHue));
      // A detail page is only reachable for a seen species and the default tile
      // is the base catch; treat a confident silhouette as unknown rather than
      // writing the MAIN dex missing off a geometry guess.
      const state: CellState = verdict.state === "missing" ? "unknown" : verdict.state;
      keep({
        variant: "pokemon",
        state,
        label: "",
        roi: defaultRoi,
        confidence: verdict.confidence,
        signals: verdict.signals,
      });
    }
  } else if (labelBlocks.length > 0) {
    warnings.push("default_card_ungeolocated");
  }

  return Array.from(byVariant.values());
}

/** The labels sharing the topmost card row (within a row tolerance). */
function topRowLabelBlocks(labels: OcrTextBlock[]): OcrTextBlock[] {
  if (labels.length === 0) return [];
  const minY = Math.min(...labels.map((b) => b.y));
  return labels.filter((b) => b.y - minY < 0.04);
}

/**
 * Dominant hue of the page background wash. Samples a thin vertical strip down
 * the left edge (beside the model/title, clear of content across scroll
 * positions) and returns the circular-mean hue of its chromatic pixels, or null
 * when the strip is achromatic. Drives the wash-relative silhouette rule.
 */
function pageWashHue(img: HTMLImageElement, deps: DetailOcrDeps): number | null {
  const strip = deps.cropRgba(img, { x: 0.01, y: 0.15, w: 0.06, h: 0.37 });
  if (!strip) return null;
  const { data } = strip;
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    const r = data[i] / 255;
    const g = data[i + 1] / 255;
    const b = data[i + 2] / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    if (d < 0.03) continue; // near-grey: no usable hue
    let hh: number;
    if (max === r) hh = ((g - b) / d) % 6;
    else if (max === g) hh = (b - r) / d + 2;
    else hh = (r - g) / d + 4;
    hh *= 60;
    if (hh < 0) hh += 360;
    const rad = (hh * Math.PI) / 180;
    sx += Math.cos(rad);
    sy += Math.sin(rad);
    n += 1;
  }
  if (n === 0) return null;
  let hue = (Math.atan2(sy, sx) * 180) / Math.PI;
  if (hue < 0) hue += 360;
  return hue;
}

function mapBlockToFrame(b: OcrTextBlock, roi: OcrRegion): OcrTextBlock {
  return {
    ...b,
    x: roi.x + b.x * roi.w,
    y: roi.y + b.y * roi.h,
    w: b.w * roi.w,
    h: b.h * roi.h,
  };
}

function defaultDeps(): DetailOcrDeps {
  return {
    loadImage: loadImageFromDataUrl,
    ocrBlocks: async (img, roi) => {
      const [res] = await recognizeRegions(img, [roi], { multiline: true });
      return res?.blocks ?? [];
    },
    cropRgba: cropRgbaFromImage,
  };
}

function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  // One decode per frame, shared by every consumer (lib/frameImageCache).
  return loadFrameImage(dataUrl);
}

function cropRgbaFromImage(
  img: HTMLImageElement,
  roi: OcrRegion,
): { data: Uint8ClampedArray; width: number; height: number } | null {
  const W = img.naturalWidth || img.width;
  const H = img.naturalHeight || img.height;
  const x = Math.max(0, Math.floor(roi.x * W));
  const y = Math.max(0, Math.floor(roi.y * H));
  const w = Math.max(1, Math.min(W - x, Math.floor(roi.w * W)));
  const h = Math.max(1, Math.min(H - y, Math.floor(roi.h * H)));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return null;
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h);
  return { data: d.data, width: w, height: h };
}

function clampRoi(r: OcrRegion): OcrRegion {
  const x = Math.min(Math.max(0, r.x), 1);
  const y = Math.min(Math.max(0, r.y), 1);
  return {
    x,
    y,
    w: Math.min(r.w, 1 - x),
    h: Math.min(r.h, 1 - y),
  };
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}
function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}
